import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '@environments/environment';

import { catchError, mergeMap, tap } from 'rxjs/operators';
import { forkJoin, throwError } from 'rxjs';

import { FormPatientNephroService } from '@services/form/form-patient-nephro.service';
import { SidebarService } from '@ui/sidebar';
import { addDays } from 'date-fns';
import { BackPageService } from '@services/back-page/back-page.service';
import { AuthService } from '../security';
import { PatientService } from '../patient';
import { ErrorFormService } from './error-form.service';
import { FormatFormService } from './format-form.service';

@Injectable({ providedIn: 'root' })
export class ReinclusionNephroService {
    patient;
    comment = '';

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private patientService: PatientService,
        private errorService: ErrorFormService,
        private router: Router,
        private formatService: FormatFormService,
        private formPatientNephroService: FormPatientNephroService,
        private sidebarService: SidebarService,
        private backPageService: BackPageService,
    ) {}

    initReinclusion(id) {
        this.errorService.resetErrors();
        return this.initPatient(id).pipe(mergeMap(() => this.initUsers()));
    }

    initUsers() {
        const promises = [
            this.formPatientNephroService.initSpecialistsFromPatient(
                this.patient,
            ),
        ];
        if (this.authService.isAdmin) promises.push(this.formPatientNephroService.initCompanies());
        if (this.authService.canEditMedicalInfos) {
            promises.push(
                this.formPatientNephroService.loadImmunosuppressants(),
            );
        }
        return forkJoin(promises);
    }

    initPatient(id) {
        return this.patientService.getPatient(id, [], 'nephro').pipe(
            tap((patient) => {
                this.patient = this.formatService.initPatient(
                    patient,
                    'nephro',
                );
                this.patient.inclusion.prescription = false;
                this.patient.inclusion.type = 'commonLaw';
                this.patient.inclusion.prescriptionFileName = '';
                this.patient.inclusion.prescriptionFileType = '';
                this.patient.inclusion.startDate = this.getPrescriptionDate();
                this.comment = '';
            }),
        );
    }

    getPrescriptionDate() {
        if (this.patient.inclusion.toRenew) {
            return addDays(
                this.patient.inclusion.endDate,
                1,
            ).getTime();
        }
        if (this.patient.inclusion.toReinclude) {
            return new Date().getTime();
        }
    }

    canSavePatient() {
        return (
            this.formPatientNephroService.checkInclusionModePatient(
                this.patient,
            ) &&
            this.formPatientNephroService.checkMedicalDataPatient(
                this.patient,
            ) &&
            (!this.authService.isAdmin ||
                this.formPatientNephroService.checkCarestaffPatient(
                    this.patient,
                ))
        );
    }

    reinclusionPatient() {
        if (this.patient.demo) {
            this.sidebarService.show();
            this.backPageService.goBackPage();
            return;
        }

        const formData = this.formatService.formatPatientNephro(this.patient);
        formData.append('comment', this.comment);

        return this.reinclusionPatientRequest(formData).pipe(
            tap(() => {
                this.sidebarService.show();
                this.backPageService.goBackPage();
            }),
        );
    }

    reinclusionPatientRequest(formData) {
        return this.http
            .put<any>(
                `${environment.apiUrl}/patient/${this.patient._id}/reinclusion`,
                formData,
                {
                    params: { disease: 'nephro' },
                },
            )
            .pipe(
                catchError((err) => {
                    this.errorService.initErrors(err?.error?.errors);
                    return throwError(() => err);
                }),
            );
    }
}
